import { memo } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';

import type {
	BottleneckBoardResponse,
	BottleneckSection,
} from '../../../../redux/api/bottleneck-report';
import { formatCount, formatDuration, formatMoney } from './buckets';

interface PrintableBoardProps {
	data: BottleneckBoardResponse;
	/** The job-type filter the numbers were produced under. */
	jobType: string;
	totals: { lineItems: number; parts: number; revenue: number };
}

const timestamp = (generatedAt: string) =>
	dayjs(generatedAt).format('MMM D, YYYY HH:mm');

const SummaryTable = ({
	sections,
	showRevenue,
}: {
	sections: BottleneckSection[];
	showRevenue: boolean;
}) => (
	<table className="bottleneck-print__data">
		<thead>
			<tr>
				<th>Stage</th>
				<th className="bottleneck-print__num">Line items</th>
				<th className="bottleneck-print__num">Ticketed</th>
				<th className="bottleneck-print__num">Parts</th>
				{showRevenue && <th className="bottleneck-print__num">Revenue</th>}
				<th className="bottleneck-print__num">25%</th>
				<th className="bottleneck-print__num">50%</th>
				<th className="bottleneck-print__num">75%</th>
				<th className="bottleneck-print__num">Max</th>
				<th className="bottleneck-print__num">Samples</th>
			</tr>
		</thead>
		<tbody>
			{sections.map((section) => {
				const { quartiles } = section;
				const measured = quartiles.sample_count > 0;
				const duration = (seconds: number | null) =>
					measured ? formatDuration(seconds) : '—';
				return (
					<tr key={section.key}>
						<td>
							{section.label}
							{section.quartiles_basis === 'lead_time' && ' (lead time)'}
						</td>
						<td className="bottleneck-print__num">
							{formatCount(section.line_item_count)}
						</td>
						<td className="bottleneck-print__num">
							{section.ticketed_line_item_count || '—'}
						</td>
						<td className="bottleneck-print__num">
							{formatCount(section.quantity)}
						</td>
						{showRevenue && (
							<td className="bottleneck-print__num">
								{formatMoney(section.revenue)}
							</td>
						)}
						<td className="bottleneck-print__num">
							{duration(quartiles.p25_seconds)}
						</td>
						<td className="bottleneck-print__num">
							{duration(quartiles.p50_seconds)}
						</td>
						<td className="bottleneck-print__num">
							{duration(quartiles.p75_seconds)}
						</td>
						<td className="bottleneck-print__num">
							{duration(quartiles.p100_seconds)}
						</td>
						<td className="bottleneck-print__num">
							{measured ? formatCount(quartiles.sample_count) : '—'}
						</td>
					</tr>
				);
			})}
		</tbody>
	</table>
);

const StageTable = ({
	section,
	showRevenue,
}: {
	section: BottleneckSection;
	showRevenue: boolean;
}) => (
	<div className="bottleneck-print__stage">
		<table className="bottleneck-print__data">
			{/*
			 * The stage name is a header row rather than a heading above the
			 * table because browsers repeat `thead` on every page a table
			 * spans. A long stage therefore names itself again after a page
			 * break instead of continuing as an unlabelled list of jobs.
			 */}
			<thead>
				<tr>
					<th
						className="bottleneck-print__stage-heading"
						colSpan={showRevenue ? 8 : 7}
					>
						{section.label} — {formatCount(section.line_item_count)} line items
						· {formatCount(section.quantity)} parts
						{showRevenue && ` · ${formatMoney(section.revenue)}`}
						{section.ticketed_line_item_count > 0 &&
							` · ${formatCount(
								section.ticketed_line_item_count
							)} with open tickets`}
					</th>
				</tr>
				<tr>
					<th>Order</th>
					<th>Part</th>
					<th className="bottleneck-print__num">Qty</th>
					{showRevenue && <th className="bottleneck-print__num">Revenue</th>}
					<th className="bottleneck-print__num">Due</th>
					{/*
					 * Paper cannot be hovered, so where the screen shows a warning
					 * glyph and hides the ids in a tooltip, print spells them out.
					 */}
					<th className="bottleneck-print__num">Ticket</th>
					<th className="bottleneck-print__num">In stage</th>
					<th className="bottleneck-print__num">Total age</th>
				</tr>
			</thead>
			<tbody>
				{section.rows.map((row) => {
					// The bars are dropped in print, so their one number that is
					// not already in a column — total age — becomes a column.
					const age = row.segments
						.filter((segment) => segment.bucket !== 'remaining')
						.reduce((total, segment) => total + segment.seconds, 0);
					const dueDate = row.shop_due_date ?? row.required_part_ship_date;
					return (
						<tr key={row.id}>
							<td>{row.order_name || '—'}</td>
							<td>{row.name}</td>
							<td className="bottleneck-print__num">
								{formatCount(row.quantity)}
							</td>
							{showRevenue && (
								<td className="bottleneck-print__num">
									{formatMoney(row.revenue)}
								</td>
							)}
							<td
								className={`bottleneck-print__num${
									row.is_overdue ? ' bottleneck-print__overdue' : ''
								}`}
							>
								{dueDate ? dayjs(dueDate).format('MMM D, YYYY') : '—'}
							</td>
							<td className="bottleneck-print__num">
								{row.open_ticket_uids?.length
									? row.open_ticket_uids.join(', ')
									: '—'}
							</td>
							<td className="bottleneck-print__num">
								{formatDuration(row.current_dwell_seconds)}
							</td>
							<td className="bottleneck-print__num">{formatDuration(age)}</td>
						</tr>
					);
				})}
			</tbody>
		</table>
	</div>
);

/**
 * The board as a portrait document: the twelve stage summaries, then every job
 * listed under its stage.
 *
 * A separate DOM tree rather than print styles over the live board, for two
 * reasons. Closed antd panels are not in the layout, so printing the screen
 * would quietly omit whichever stages the reader had not opened. And the
 * running header and footer need a real table — browsers repeat `thead` and
 * `tfoot` across pages, which is what puts the header on page four.
 *
 * Timeline bars are left out: they need horizontal room portrait does not
 * have, and the durations beside them carry the same information.
 */
const PrintableBoard = ({ data, jobType, totals }: PrintableBoardProps) => {
	const showRevenue = data.include_revenue;
	const populated = data.sections.filter((section) => section.rows.length > 0);
	const generated = timestamp(data.generated_at);
	// The screen hedges a truncated board in the headline and again in a
	// warning banner, both of which live in the app shell that print hides. On
	// paper the numbers would otherwise read as an exact census when they are
	// floors, which is the one reading this report must not invite.
	const truncated = data.live_truncated;

	return createPortal(
		<div className="bottleneck-print">
			<table className="bottleneck-print__page">
				<thead>
					<tr>
						<td className="bottleneck-print__header">
							<div className="bottleneck-print__title">Job Bottleneck</div>
							<div className="bottleneck-print__meta">
								<span>
									{jobType} jobs · {truncated ? 'at least ' : ''}
									{formatCount(totals.lineItems)} line items ·{' '}
									{formatCount(totals.parts)} parts
									{showRevenue && ` · ${formatMoney(totals.revenue)}`} still in
									the shop
								</span>
								<span>{generated}</span>
							</div>
						</td>
					</tr>
				</thead>

				<tfoot>
					<tr>
						<td className="bottleneck-print__footer">
							PartsBadger · Job Bottleneck · {generated} · Delivered shows the
							last {data.delivered_limit} shipped;{' '}
							{truncated
								? `live work was capped at the first ${formatCount(
										data.live_limit
								  )} jobs, so every count here is a floor, not a total.`
								: 'every other stage is all outstanding work.'}
						</td>
					</tr>
				</tfoot>

				<tbody>
					<tr>
						<td className="bottleneck-print__body">
							<SummaryTable
								sections={data.sections}
								showRevenue={showRevenue}
							/>

							{populated.map((section) => (
								<StageTable
									key={section.key}
									section={section}
									showRevenue={showRevenue}
								/>
							))}

							<p className="bottleneck-print__note">
								Stages are evaluated in a fixed order and a job lands in the
								first one it matches, so no job is counted twice. Cancelled,
								on-hold, completed and rejected work is left out.
							</p>
						</td>
					</tr>
				</tbody>
			</table>
		</div>,
		document.body
	);
};

export default memo(PrintableBoard);
